import * as _ from 'lodash';
import { Document } from 'src/app/people/models/document';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { Image } from 'src/app/people/models/image';
import { Tag } from 'src/app/people/models/tag';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { PersonStatus } from 'src/app/people/models/personStatus';
import { PersonDisclaimer } from 'src/app/people/models/personDisclaimer';
import { Disclaimer } from 'src/app/shared/models/disclaimer';
import { StringIgnoreCaseComparator } from 'src/app/shared/utils/comparators';

export class PersonForMemberRequests {
  public id: string;
  public firstName: string;
  public lastName: string;
  public middleName: string;
  public suffix: string;
  public fullName: string;
  public email: string;
  public linkedInProfile: string;
  public jobTitle: string;
  public company: string;
  public image: Image;
  public phoneNumber: string;
  public extension: string;
  public personInvoiceId: string;
  public rating: number;
  public documents: Document[] = new Array<Document>();
  public demographicInfo: DemographicInfo = new DemographicInfo();
  public tags: Tag[];
  public attributes: Attribute[] = new Array<Attribute>();
  public disclaimers: PersonDisclaimer[] = new Array<PersonDisclaimer>();
  public updatedOn: Date;
  public status: string;
  public metadata: any;

  constructor(person: any = undefined) {
    if (person) this.setProperties(person);
  }
  public get certMetricsId(): string {
    return this.metadata?.certMetricsId;
  }

  public set certMetricsId(value: string) {
    this.metadata = this.metadata ?? {};
    this.metadata.certMetricsId = value;
  }
  isArchived() {
    return this.status == PersonStatus.Archived;
  }

  isActive() {
    return this.status == PersonStatus.Active;
  }

  isPotential() {
    return this.status == PersonStatus.Potential;
  }

  setImage(person: any) {
    if (person.image) {
      this.image = person.image;
    } else {
      this.image = new Image(person.imageId);
    }
  }

  setTags(tags: Tag[]) {
    this.tags = _.map(tags, function (tag) {
      return new Tag(tag);
    });
  }

  addTag(tag: Tag) {
    this.tags.push(tag);
    this.tags = _.sortBy(this.tags, 'text');
  }

  setDisclaimers(disclaimers: PersonDisclaimer[]) {
    this.disclaimers = _.map(disclaimers, (disclaimer) => PersonDisclaimer.parse(disclaimer));
  }

  getPersonDisclaimer(disclaimer: Disclaimer) {
    return _.find(this.disclaimers, function (personDisclaimer) {
      if (disclaimer.id === personDisclaimer.disclaimer.id) return true;
    });
  }

  hasDisclaimer(disclaimer: Disclaimer): boolean {
    return !_.isNil(this.getPersonDisclaimer(disclaimer));
  }

  deleteDocument(documentToRemove: Document) {
    _.remove(this.documents, function (document) {
      return documentToRemove.key == document.key;
    });
  }

  hasRace() {
    return (
      this.demographicInfo && this.demographicInfo.races && this.demographicInfo.races.length > 0
    );
  }

  hasImage() {
    return this.image && this.image.id;
  }

  hasDocuments() {
    return this.documents && this.documents.length;
  }

  isMaleGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'male'
    );
  }

  isFemaleGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'female'
    );
  }

  isNonBinaryGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'nonbinary'
    );
  }

  isUnknownGender() {
    return (
      !this.isMaleGender() &&
      !this.isFemaleGender() &&
      !this.isNonBinaryGender() &&
      !this.isPreferNotToAnswerGender()
    );
  }

  isPreferNotToAnswerGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'prefernottoanswer'
    );
  }

  getPhoneNumberForDisplay() {
    if (Number.parseInt(this.phoneNumber).toString().length === this.phoneNumber.length) {
      return `(${this.phoneNumber.substring(0, 3)}) ${this.phoneNumber.substring(
        3,
        6
      )}-${this.phoneNumber.substring(6, 10)}`;
    }

    return this.phoneNumber ? this.phoneNumber : '';
  }

  hasAllTags(tags: Tag[]) {
    return _.differenceBy(tags, this.tags, 'id').length === 0;
  }

  hasRating() {
    return this.rating && this.rating > 0;
  }

  getStarRatingPercentage() {
    return (this.rating / 5) * 100;
  }

  setAttribute(attributeDefinitionId: string, attributes: Attribute[]) {
    let filtered = this.attributes.filter(
      (attribute) => attribute.attributeDefinitionId != attributeDefinitionId
    );
    let merged = filtered.concat(
      attributes.filter((attribute) => attribute.attributeDefinitionId == attributeDefinitionId)
    );
    let ordered = merged.sort(StringIgnoreCaseComparator('name'));
    this.attributes = ordered;
  }

  setAttributes(attributes) {
    attributes = _.filter(attributes, (a) => a != null && a != undefined);
    this.attributes = _.orderBy(
      _.map(attributes, function (attribute) {
        return Attribute.parse(attribute);
      }),
      'name'
    );
  }

  private setProperties(person: any) {
    this.id = person.id;
    this.firstName = person.firstName;
    this.lastName = person.lastName;
    this.middleName = person.middleName;
    this.suffix = person.suffix;
    const middleName = person.middleName ? ` ${person.middleName} ` : '';
    const suffix = person.suffix ? ` ${person.suffix}` : '';
    this.fullName = `${person.firstName}${middleName}${person.lastName}${suffix}`;
    this.email = person.email;
    this.phoneNumber = person.phoneNumber;
    this.extension = person.extension;
    this.linkedInProfile = person.linkedInProfile;
    this.jobTitle = person.jobTitle;
    this.company = person.company;
    this.personInvoiceId = person.personInvoiceId;
    this.status = person.status;
    this.rating = person.rating;
    this.setAttributes([].concat(person.customFields, person.trackedFields));
    this.setImage(person);
    this.demographicInfo = new DemographicInfo(person.demographicInfo);
    this.setTags(person.tags);
    this.updatedOn = person.updatedOn;
    this.metadata = person.metadata;
  }
}
